import { z } from 'zod';
import { IsoDateTime, Uuid } from './common';

// ---- Trash ("Lixeira") -------------------------------------------------------------------------

export const TrashType = z.enum([
  'customer',
  'quote',
  'product',
  'variant',
  'product_file',
  'media',
  'listing',
  'filament',
  'spool',
  'supply',
  'packaging_kit',
  'expense',
  'recurring_expense',
  'channel',
  'printer',
  'maintenance_task',
  'message_template',
  'file',
]);
export type TrashType = z.infer<typeof TrashType>;
export const TRASH_TYPE_LABELS: Record<TrashType, string> = {
  customer: 'Cliente',
  quote: 'Orçamento',
  product: 'Produto',
  variant: 'Variação',
  product_file: 'Arquivo do produto',
  media: 'Foto ou vídeo',
  listing: 'Anúncio',
  filament: 'Filamento',
  spool: 'Carretel',
  supply: 'Insumo',
  packaging_kit: 'Kit de embalagem',
  expense: 'Despesa',
  recurring_expense: 'Recorrente',
  channel: 'Canal',
  printer: 'Impressora',
  maintenance_task: 'Tarefa de manutenção',
  message_template: 'Modelo de mensagem',
  file: 'Anexo',
};
/** Days a deleted record stays restorable. */
export const TRASH_DAYS = 30;

export const TrashItem = z
  .object({
    type: TrashType,
    id: Uuid,
    name: z.string(),
    detail: z.string().nullable().describe('Context, e.g. the product of a variant.'),
    deletedAt: IsoDateTime,
    deletedBy: z.string().nullable(),
    daysLeft: z.number().int(),
  })
  .meta({ id: 'TrashItem' });
export type TrashItem = z.infer<typeof TrashItem>;

export const TrashQuery = z.object({ type: TrashType.optional() });
export const TrashParams = z.object({ type: TrashType, id: Uuid });
export const TrashList = z
  .object({
    data: z.array(TrashItem),
    counts: z.record(z.string(), z.number().int()).describe('Items per type.'),
  })
  .meta({ id: 'TrashList' });
export type TrashList = z.infer<typeof TrashList>;
export const TrashEmptyResult = z.object({
  removed: z.number().int(),
  kept: z.number().int().describe('Still used by other records: kept for history, hidden.'),
});

// ---- Audit ("Auditoria") ---------------------------------------------------------------------

export const AuditCategory = z.enum(['precos', 'pedidos', 'exclusoes', 'outros']);
export type AuditCategory = z.infer<typeof AuditCategory>;
export const AUDIT_CATEGORY_LABELS: Record<AuditCategory, string> = {
  precos: 'Preços e parâmetros',
  pedidos: 'Pedidos',
  exclusoes: 'Exclusões',
  outros: 'Outros',
};

export const AuditEntry = z
  .object({
    id: Uuid,
    at: IsoDateTime,
    actor: z.string().describe('Person, "Lucas (token: n8n)" for API tokens, or "sistema".'),
    actorUserId: Uuid.nullable(),
    viaToken: z.boolean(),
    action: z.string(),
    entityType: z.string(),
    entityId: Uuid.nullable(),
    category: AuditCategory,
    title: z.string().describe('"O quê": the record, e.g. "Pedido P-2026-0124".'),
    summary: z.string().describe('"Detalhe": what changed, in pt-BR.'),
  })
  .meta({ id: 'AuditEntry' });
export type AuditEntry = z.infer<typeof AuditEntry>;

export const AuditChange = z.object({
  field: z.string(),
  label: z.string(),
  before: z.string().nullable(),
  after: z.string().nullable(),
});
export type AuditChange = z.infer<typeof AuditChange>;

export const AuditDetail = AuditEntry.extend({
  changes: z.array(AuditChange).describe('Field-by-field, formatted for reading.'),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  requestId: z.string().nullable(),
}).meta({ id: 'AuditDetail' });
export type AuditDetail = z.infer<typeof AuditDetail>;

export const AuditQuery = z.object({
  category: AuditCategory.optional(),
  actorUserId: Uuid.optional(),
  entityType: z.string().max(60).optional(),
  entityId: Uuid.optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type AuditQuery = z.infer<typeof AuditQuery>;

export const AuditList = z
  .object({
    data: z.array(AuditEntry),
    nextCursor: z.string().nullable(),
    counts: z
      .record(z.string(), z.number().int())
      .describe('Entries per category (and `all`) for the person and period filters.'),
    people: z.array(z.object({ userId: Uuid, name: z.string() })),
  })
  .meta({ id: 'AuditList' });
export type AuditList = z.infer<typeof AuditList>;

// ---- CSV import ("Importar") ------------------------------------------------------------------

export const ImportEntity = z.enum(['filamentos', 'carreteis', 'insumos', 'produtos', 'despesas']);
export type ImportEntity = z.infer<typeof ImportEntity>;
export const IMPORT_ENTITY_LABELS: Record<ImportEntity, string> = {
  filamentos: 'Filamentos',
  carreteis: 'Carretéis',
  insumos: 'Insumos',
  produtos: 'Produtos',
  despesas: 'Despesas',
};

export const ImportField = z.object({
  key: z.string(),
  label: z.string(),
  required: z.boolean(),
  hint: z.string().nullable(),
});
export type ImportField = z.infer<typeof ImportField>;

export const ImportSpec = z
  .object({
    entity: ImportEntity,
    fields: z.array(ImportField),
    keys: z
      .array(z.object({ key: z.string(), label: z.string() }))
      .describe('"Coluna-chave" options; `none` = everything comes in as new.'),
    mapping: z.record(z.string(), z.string()).describe('Last mapping used (file column → field).'),
    keyField: z.string().nullable(),
  })
  .meta({ id: 'ImportSpec' });
export type ImportSpec = z.infer<typeof ImportSpec>;

export const ImportBody = z
  .object({
    entity: ImportEntity,
    fileName: z.string().trim().min(1).max(200),
    csv: z.string().min(1).max(10_000_000).describe('The file text (`;` or `,`, UTF-8).'),
    mapping: z.record(z.string(), z.string()).describe('File column → field key, or `ignore`.'),
    keyField: z.string().nullable().default(null),
    fixes: z
      .array(z.object({ line: z.number().int().min(2), column: z.string(), value: z.string() }))
      .max(5000)
      .default([])
      .describe('Inline corrections ("Corrigir", "Editar") applied before validation.'),
    create: z
      .array(z.string())
      .max(500)
      .default([])
      .describe('Missing references to create at import (e.g. a filament name).'),
  })
  .meta({ id: 'ImportBody' });
export type ImportBody = z.input<typeof ImportBody>;

export const ImportIssue = z.object({
  line: z.number().int(),
  column: z.string(),
  value: z.string(),
  problem: z.string(),
  fix: z
    .discriminatedUnion('kind', [
      z.object({ kind: z.literal('replace'), value: z.string(), label: z.string() }),
      z.object({ kind: z.literal('create'), value: z.string(), label: z.string() }),
      z.object({ kind: z.literal('edit') }),
    ])
    .describe('"Corrigir para …", "Criar filamento" or "Editar".'),
});
export type ImportIssue = z.infer<typeof ImportIssue>;

export const ImportPreview = z
  .object({
    rows: z.number().int(),
    columns: z.array(z.string()),
    separator: z.enum([';', ',']),
    mapping: z
      .record(z.string(), z.string())
      .describe('The mapping used (guessed from the headers when none was sent).'),
    sample: z.record(z.string(), z.string()).describe('Line 2 per file column.'),
    mappingErrors: z.array(z.string()),
    counts: z.object({
      new: z.number().int(),
      update: z.number().int(),
      errors: z.number().int(),
    }),
    issues: z.array(ImportIssue),
    preview: z.array(
      z.object({
        line: z.number().int(),
        action: z.enum(['new', 'update']),
        values: z.array(z.string()),
      }),
    ),
    previewHeader: z.array(z.string()),
    notes: z.array(z.string()).describe('What will also happen (e.g. filaments created).'),
  })
  .meta({ id: 'ImportPreview' });
export type ImportPreview = z.infer<typeof ImportPreview>;

export const ImportRecord = z
  .object({
    id: Uuid,
    entity: ImportEntity,
    fileName: z.string(),
    newCount: z.number().int(),
    updatedCount: z.number().int(),
    skippedCount: z.number().int(),
    status: z.enum(['done', 'undone']),
    actor: z.string(),
    createdAt: IsoDateTime,
    undoneAt: IsoDateTime.nullable(),
    undoUntil: IsoDateTime.describe('"Dá para desfazer até" (30 days).'),
    undoable: z.boolean().describe('Done and still within the 30 days.'),
  })
  .meta({ id: 'ImportRecord' });
export type ImportRecord = z.infer<typeof ImportRecord>;

// ---- Dashboard ("Início") --------------------------------------------------------------------

const NeedTile = z.object({
  count: z.number().int(),
  nearestOn: z.iso.date().nullable().describe('Nearest due / ship-by date.'),
  late: z.boolean(),
  single: z
    .object({ id: Uuid, number: z.string() })
    .nullable()
    .describe('When there is exactly one, which one.'),
});

export const Dashboard = z
  .object({
    today: z.iso.date(),
    calm: z.boolean().describe('Nothing to print, pack, ship or answer.'),
    needs: z.object({
      toPrint: NeedTile.describe('Queued print jobs for orders.'),
      toPack: NeedTile.describe('Orders "Para embalar" not packed yet.'),
      toShip: NeedTile.describe('Orders packed, waiting to ship.'),
      quotes: z.object({
        count: z.number().int().describe('Sent quotes without an answer.'),
        expiringSoon: z.number().int().describe('Expiring today or tomorrow.'),
      }),
    }),
    printers: z.array(
      z.object({
        id: Uuid,
        name: z.string(),
        status: z.enum(['idle', 'printing', 'maintenance', 'out_of_service']),
        current: z
          .object({
            jobId: Uuid,
            number: z.string(),
            label: z.string(),
            progressPct: z.number().int().nullable(),
            endsAt: IsoDateTime.nullable(),
          })
          .nullable(),
        next: z
          .object({ jobId: Uuid, number: z.string(), label: z.string(), seconds: z.number().int() })
          .nullable(),
        suggestion: z
          .object({
            variantId: Uuid,
            name: z.string(),
            available: z.number().int(),
            perWeek: z.number(),
          })
          .nullable()
          .describe('Free printer: a variant selling faster than its stock covers (2 weeks).'),
      }),
    ),
    month: z.object({
      month: z.string(),
      revenueCentavos: z.number().int(),
      variableCentavos: z.number().int(),
      contributionCentavos: z.number().int(),
      fixedCentavos: z.number().int(),
      netProfitCentavos: z.number().int(),
      fillBps: z.number().int(),
      reached: z.boolean(),
      unitsMissing: z.number().int().nullable(),
    }),
    openQuotes: z.array(
      z.object({
        id: Uuid,
        number: z.string(),
        what: z.string(),
        who: z.string().nullable(),
        sentAt: IsoDateTime.nullable(),
        validUntil: z.iso.date(),
      }),
    ),
    lowStock: z.array(
      z.object({
        kind: z.enum(['filament', 'supply']),
        name: z.string(),
        current: z.number(),
        minimum: z.number(),
        unit: z.string(),
      }),
    ),
    maintenance: z.array(
      z.object({
        kind: z.enum(['task', 'installment']),
        label: z.string(),
        badge: z.string(),
        tone: z.enum(['err', 'warn', 'info']),
        link: z.string(),
      }),
    ),
    activity: z.array(
      z.object({
        at: IsoDateTime,
        text: z.string(),
        link: z.string(),
        tone: z.enum(['normal', 'err']),
      }),
    ),
    lastActivityOn: z.iso.date().nullable(),
  })
  .meta({ id: 'Dashboard' });
export type Dashboard = z.infer<typeof Dashboard>;

// ---- Global search (Topbar, Shell-Celular) ------------------------------------------------------

export const SearchKind = z.enum(['product', 'sku', 'customer', 'quote', 'order', 'spool']);
export type SearchKind = z.infer<typeof SearchKind>;
export const SEARCH_KIND_LABELS: Record<SearchKind, string> = {
  product: 'Produto',
  sku: 'SKU',
  customer: 'Cliente',
  quote: 'Orçamento',
  order: 'Pedido',
  spool: 'Carretel',
};
export const SearchQuery = z.object({
  q: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .describe(
      'Name, SKU, customer, quote or order number, marketplace order number or spool code.',
    ),
  limit: z.coerce.number().int().min(1).max(30).default(8),
});
export const SearchResult = z.object({
  kind: SearchKind,
  id: Uuid,
  label: z.string().describe('"Vaso Ondas", "VAS-OND-M", "Q-2026-0041", "C-0009"…'),
  sub: z.string().describe('"VAS-OND · 3 variações", "PLA Basic Branco · 180 g"…'),
  href: z.string().describe('Web app path to open it.'),
});
export type SearchResult = z.infer<typeof SearchResult>;
export const SearchResults = z
  .object({ data: z.array(SearchResult) })
  .meta({ id: 'SearchResults' });

// ---- Sidebar counts (Sidebar, Shell-Celular "Mais") ---------------------------------------------

export const NavCounts = z
  .object({
    quotes: z.number().int().describe('Sent quotes without an answer (not expired).'),
    orders: z.number().int().describe('Open orders: Novo, Em produção, Para embalar.'),
    filaments: z.number().int().describe('Filaments below their minimum.'),
  })
  .meta({ id: 'NavCounts' });
export type NavCounts = z.infer<typeof NavCounts>;
